/**
 * TapperScroller Component for Poker Reborn
 *
 * A horizontal scrollable container for fixed-width items (Tappers).
 * Like Netflix thumbnails - drag/swipe to scroll through items.
 *
 * @see docs/screens/lobby/TAPPER_SCROLLER.md - Visual specs, snap modes, physics
 */

import { Container, Graphics, FederatedPointerEvent } from 'pixi.js';

// =============================================================================
// TYPES
// =============================================================================

export type SnapMode = 'none' | 'item' | 'center';

export interface TapperScrollerStyle {
  /** Background color (0 for transparent) */
  backgroundColor: number;

  /** Background alpha */
  backgroundAlpha: number;
}

export interface TapperScrollerOptions {
  /** Viewport width */
  width: number;

  /** Viewport height */
  height: number;

  /** Width of each tapper item (default: 120) */
  tapperWidth?: number;

  /** Gap between tappers (default: 16) */
  gap?: number;

  /** Snap behavior: 'none' | 'item' | 'center' (default: 'none') */
  snapMode?: SnapMode;

  /** Friction for momentum (0-1, higher = longer coast, default: 0.95) */
  friction?: number;

  /** Padding on left/right edges (default: 16) */
  edgePadding?: number;

  /** Custom style overrides */
  style?: Partial<TapperScrollerStyle>;
}

// =============================================================================
// DEFAULT STYLE
// =============================================================================

const DEFAULT_STYLE: TapperScrollerStyle = {
  backgroundColor: 0x000000,
  backgroundAlpha: 0,
};

// =============================================================================
// TAPPER SCROLLER COMPONENT
// =============================================================================

export class TapperScroller extends Container {
  private style: TapperScrollerStyle;
  private viewportWidth: number;
  private viewportHeight: number;
  private tapperWidth: number;
  private gap: number;
  private _snapMode: SnapMode;
  private friction: number;
  private edgePadding: number;

  // Visual elements
  private background: Graphics;
  private contentContainer: Container;
  private contentMask: Graphics;

  // Tappers
  private tappers: Container[] = [];

  // Drag state
  private isDragging = false;
  private dragStartX = 0;
  private dragStartScrollX = 0;
  private lastPointerX = 0;
  private lastPointerTime = 0;
  private velocity = 0;
  private velocitySamples: { v: number; t: number }[] = [];
  private dragDistance = 0;
  private readonly dragThreshold = 5; // Pixels before considered a drag (not tap)
  private swipeDirection: -1 | 0 | 1 = 0; // -1 = left (next), 0 = none, 1 = right (prev)

  // Animation state
  private animationId: number | null = null;
  private scrollX = 0; // Current scroll position (negative = scrolled right)

  /** Callback when scroll position changes */
  public onScroll?: (scrollX: number) => void;

  constructor(options: TapperScrollerOptions) {
    super();

    this.style = { ...DEFAULT_STYLE, ...options.style };
    this.viewportWidth = options.width;
    this.viewportHeight = options.height;
    this.tapperWidth = options.tapperWidth ?? 120;
    this.gap = options.gap ?? 16;
    this._snapMode = options.snapMode ?? 'none';
    this.friction = options.friction ?? 0.95;
    this.edgePadding = options.edgePadding ?? 16;

    // Create background
    this.background = new Graphics();
    this.addChild(this.background);

    // Create mask for clipping
    this.contentMask = new Graphics();
    this.addChild(this.contentMask);

    // Create content container (this is what moves)
    this.contentContainer = new Container();
    this.contentContainer.mask = this.contentMask;
    this.addChild(this.contentContainer);

    // Setup interactions
    this.setupInteractions();

    // Initial draw
    this.draw();
  }

  // ===========================================================================
  // PUBLIC API
  // ===========================================================================

  /** Get current snap mode */
  get snapMode(): SnapMode {
    return this._snapMode;
  }

  /** Set snap mode */
  set snapMode(mode: SnapMode) {
    this._snapMode = mode;
  }

  /** Add a tapper to the scroller */
  addTapper(tapper: Container): void {
    this.tappers.push(tapper);
    this.contentContainer.addChild(tapper);
    this.layoutTappers();
  }

  /** Remove a tapper from the scroller */
  removeTapper(tapper: Container): void {
    const index = this.tappers.indexOf(tapper);
    if (index !== -1) {
      this.tappers.splice(index, 1);
      this.contentContainer.removeChild(tapper);
      this.layoutTappers();
    }
  }

  /** Remove all tappers */
  clearTappers(): void {
    for (const tapper of this.tappers) {
      this.contentContainer.removeChild(tapper);
    }
    this.tappers = [];
    this.scrollX = 0;
    this.contentContainer.x = 0;
  }

  /** Get tapper at index */
  getTapper(index: number): Container | undefined {
    return this.tappers[index];
  }

  /** Get number of tappers */
  get tapperCount(): number {
    return this.tappers.length;
  }

  /** Scroll to specific tapper index */
  scrollTo(index: number, animate = true): void {
    if (index < 0 || index >= this.tappers.length) return;

    const targetX = this.getScrollXForIndex(index);
    const clampedX = this.clampScrollX(targetX);

    if (animate) {
      this.animateScrollTo(clampedX);
    } else {
      this.scrollX = clampedX;
      this.contentContainer.x = this.scrollX;
      this.onScroll?.(this.scrollX);
    }
  }

  /** Resize the scroller */
  resize(width: number, height: number): void {
    this.viewportWidth = width;
    this.viewportHeight = height;
    this.draw();
    this.layoutTappers();

    // Re-clamp scroll position
    this.scrollX = this.clampScrollX(this.scrollX);
    this.contentContainer.x = this.scrollX;
  }

  /** Get current scroll position */
  getScrollX(): number {
    return this.scrollX;
  }

  // ===========================================================================
  // PRIVATE METHODS
  // ===========================================================================

  private setupInteractions(): void {
    this.eventMode = 'static';
    this.cursor = 'grab';

    // Hit area covers entire viewport
    this.hitArea = {
      contains: (x: number, y: number) => {
        return x >= 0 && x <= this.viewportWidth && y >= 0 && y <= this.viewportHeight;
      },
    };

    this.on('pointerdown', this.onPointerDown, this);
    this.on('pointermove', this.onPointerMove, this);
    this.on('pointerup', this.onPointerUp, this);
    this.on('pointerupoutside', this.onPointerUp, this);
  }

  private onPointerDown(event: FederatedPointerEvent): void {
    // Cancel any ongoing animation
    this.stopAnimation();

    this.isDragging = true;
    this.cursor = 'grabbing';
    this.dragDistance = 0;

    const local = this.toLocal(event.global);
    this.dragStartX = local.x;
    this.dragStartScrollX = this.scrollX;
    this.lastPointerX = local.x;
    this.lastPointerTime = performance.now();
    this.velocity = 0;
    this.velocitySamples = [];
  }

  private onPointerMove(event: FederatedPointerEvent): void {
    if (!this.isDragging) return;

    const local = this.toLocal(event.global);
    const deltaX = local.x - this.dragStartX;

    // Track drag distance to distinguish tap from drag
    this.dragDistance += Math.abs(local.x - this.lastPointerX);
    if (this.dragDistance > this.dragThreshold) {
      // Disable button clicks while dragging
      this.contentContainer.interactiveChildren = false;
    }

    // Track velocity samples for smoothing (keep last 100ms of data)
    const currentTime = performance.now();
    const timeDelta = currentTime - this.lastPointerTime;
    if (timeDelta > 0 && timeDelta < 100) {
      this.velocitySamples.push({
        v: (local.x - this.lastPointerX) / timeDelta,
        t: currentTime,
      });
      const cutoff = currentTime - 100;
      while (this.velocitySamples.length > 0) {
        const oldest = this.velocitySamples[0];
        if (!oldest || oldest.t >= cutoff) break;
        this.velocitySamples.shift();
      }
    }
    this.lastPointerX = local.x;
    this.lastPointerTime = currentTime;

    // Calculate new scroll position
    let newScrollX = this.dragStartScrollX + deltaX;

    // Apply edge resistance
    const minScrollX = this.getMinScrollX();
    const maxScrollX = this.getMaxScrollX();

    if (newScrollX > maxScrollX) {
      // Resistance on left edge (scrolled too far left)
      newScrollX = maxScrollX + (newScrollX - maxScrollX) * 0.3;
    } else if (newScrollX < minScrollX) {
      // Resistance on right edge (scrolled too far right)
      newScrollX = minScrollX + (newScrollX - minScrollX) * 0.3;
    }

    this.scrollX = newScrollX;
    this.contentContainer.x = this.scrollX;
    this.onScroll?.(this.scrollX);
  }

  private onPointerUp(): void {
    if (!this.isDragging) return;

    this.isDragging = false;
    this.cursor = 'grab';

    // Restore button interactivity after drag ends
    this.contentContainer.interactiveChildren = true;

    // If the pointer barely moved, it was a tap — don't scroll or snap
    const wasDrag = this.dragDistance > this.dragThreshold;
    this.dragDistance = 0;

    if (!wasDrag) return;

    // Compute smoothed velocity from recent samples
    this.velocity = this.computeSmoothedVelocity();

    // Record swipe direction for snap-to-next behavior
    const minSwipeVelocity = 0.15; // px/ms — threshold for directional snap
    this.swipeDirection = this.velocity > minSwipeVelocity ? 1
      : this.velocity < -minSwipeVelocity ? -1
      : 0;

    // Apply momentum
    this.applyMomentum();
  }

  private applyMomentum(): void {
    const minScrollX = this.getMinScrollX();
    const maxScrollX = this.getMaxScrollX();
    const frameTime = 16; // ~60fps
    const snapAttractionSpeed = 1.0; // px/frame — transition to snap when this slow

    // Scale velocity to pixels per frame
    let currentVelocity = this.velocity * frameTime;

    // If velocity is very small (gentle release), snap directly
    if (Math.abs(currentVelocity) < snapAttractionSpeed) {
      this.applySnap();
      return;
    }

    const animate = () => {
      // Check if we're past bounds - bounce back
      if (this.scrollX > maxScrollX || this.scrollX < minScrollX) {
        this.bounceBack();
        return;
      }

      // Apply friction
      currentVelocity *= this.friction;

      // Transition to snap when slow enough (seamless deceleration → settle)
      if (Math.abs(currentVelocity) < snapAttractionSpeed) {
        this.animationId = null;
        this.applySnap();
        return;
      }

      // Update position
      this.scrollX += currentVelocity;

      // Edge resistance
      if (this.scrollX > maxScrollX) {
        currentVelocity *= 0.5;
        this.scrollX = maxScrollX + (this.scrollX - maxScrollX) * 0.5;
      } else if (this.scrollX < minScrollX) {
        currentVelocity *= 0.5;
        this.scrollX = minScrollX + (this.scrollX - minScrollX) * 0.5;
      }

      this.contentContainer.x = this.scrollX;
      this.onScroll?.(this.scrollX);

      this.animationId = requestAnimationFrame(animate);
    };

    this.animationId = requestAnimationFrame(animate);
  }

  private bounceBack(): void {
    const minScrollX = this.getMinScrollX();
    const maxScrollX = this.getMaxScrollX();

    let targetX = this.scrollX;
    if (this.scrollX > maxScrollX) {
      targetX = maxScrollX;
    } else if (this.scrollX < minScrollX) {
      targetX = minScrollX;
    }

    this.animateScrollTo(targetX, () => {
      this.applySnap();
    });
  }

  private applySnap(): void {
    if (this._snapMode === 'none') return;

    const targetX = this.getSnapTargetX();
    if (Math.abs(targetX - this.scrollX) > 1) {
      this.animateScrollTo(targetX);
    }
  }

  private getSnapTargetX(): number {
    if (this.tappers.length === 0) return 0;

    // When all content fits on screen, snap back to 0 (centered layout)
    const contentWidth = this.getContentWidth();
    if (contentWidth <= this.viewportWidth) return 0;

    const itemWidth = this.tapperWidth + this.gap;

    if (this._snapMode === 'item') {
      // Snap to item edge (left-aligned), biased by swipe direction
      const currentOffset = -this.scrollX + this.edgePadding;
      const fractionalIndex = currentOffset / itemWidth;
      const snapIndex = this.directionalSnap(fractionalIndex);
      const clampedIndex = Math.max(0, Math.min(snapIndex, this.tappers.length - 1));
      return this.getScrollXForIndex(clampedIndex);
    } else if (this._snapMode === 'center') {
      // Snap to centered item, biased by swipe direction
      const viewportCenter = this.viewportWidth / 2;
      const currentCenter = -this.scrollX + viewportCenter;
      const fractionalIndex = (currentCenter - this.edgePadding - this.tapperWidth / 2) / itemWidth;
      const snapIndex = this.directionalSnap(fractionalIndex);
      const clampedIndex = Math.max(0, Math.min(snapIndex, this.tappers.length - 1));
      return this.getScrollXForCentered(clampedIndex);
    }

    return this.scrollX;
  }

  /**
   * Round a fractional item index using swipe direction:
   * swipe left → ceil (next item), swipe right → floor (previous item),
   * no swipe → round (nearest item).
   */
  private directionalSnap(fractionalIndex: number): number {
    if (this.swipeDirection < 0) {
      return Math.ceil(fractionalIndex);
    } else if (this.swipeDirection > 0) {
      return Math.floor(fractionalIndex);
    }
    return Math.round(fractionalIndex);
  }

  private getScrollXForIndex(index: number): number {
    // Left-align item at index
    const itemWidth = this.tapperWidth + this.gap;
    return -(index * itemWidth) + this.edgePadding;
  }

  private getScrollXForCentered(index: number): number {
    // Center item at index
    const itemWidth = this.tapperWidth + this.gap;
    const itemCenterX = this.edgePadding + index * itemWidth + this.tapperWidth / 2;
    const viewportCenter = this.viewportWidth / 2;
    return -(itemCenterX - viewportCenter);
  }

  private animateScrollTo(targetX: number, onComplete?: () => void): void {
    this.stopAnimation();

    const startX = this.scrollX;
    const distance = targetX - startX;

    if (Math.abs(distance) < 1) {
      this.scrollX = targetX;
      this.contentContainer.x = this.scrollX;
      onComplete?.();
      return;
    }

    // Adaptive duration: proportional to distance, clamped to a pleasant range
    const duration = Math.min(500, Math.max(250, 150 + Math.abs(distance) * 0.8));
    const startTime = performance.now();

    const animate = () => {
      const elapsed = performance.now() - startTime;
      const progress = Math.min(elapsed / duration, 1);

      // Ease out quart — smoother deceleration than cubic
      const eased = 1 - Math.pow(1 - progress, 4);

      this.scrollX = startX + distance * eased;
      this.contentContainer.x = this.scrollX;
      this.onScroll?.(this.scrollX);

      if (progress < 1) {
        this.animationId = requestAnimationFrame(animate);
      } else {
        this.animationId = null;
        this.scrollX = targetX;
        this.contentContainer.x = this.scrollX;
        onComplete?.();
      }
    };

    this.animationId = requestAnimationFrame(animate);
  }

  private stopAnimation(): void {
    if (this.animationId !== null) {
      cancelAnimationFrame(this.animationId);
      this.animationId = null;
    }
  }

  /**
   * Compute a smoothed velocity from recent pointer samples.
   * Uses weighted average (recent samples weighted higher) and caps
   * the result to prevent wild flings from noisy input.
   */
  private computeSmoothedVelocity(): number {
    if (this.velocitySamples.length === 0) return 0;

    const now = performance.now();
    let weightedSum = 0;
    let totalWeight = 0;

    for (const sample of this.velocitySamples) {
      // Recent samples get higher weight (linear decay over 100ms window)
      const age = now - sample.t;
      const weight = Math.max(0, 1 - age / 100);
      weightedSum += sample.v * weight;
      totalWeight += weight;
    }

    if (totalWeight === 0) return 0;

    const smoothed = weightedSum / totalWeight;
    // Cap velocity to prevent wild flings
    const maxVelocity = 2.5; // px/ms
    return Math.max(-maxVelocity, Math.min(maxVelocity, smoothed));
  }

  private getMinScrollX(): number {
    // Maximum scroll to the right (negative value)
    const contentWidth = this.getContentWidth();
    if (contentWidth <= this.viewportWidth) {
      return 0; // No scrolling needed
    }
    return -(contentWidth - this.viewportWidth + this.edgePadding);
  }

  private getMaxScrollX(): number {
    // No scrolling when content fits
    const contentWidth = this.getContentWidth();
    if (contentWidth <= this.viewportWidth) {
      return 0;
    }
    // Minimum scroll (left edge)
    return this.edgePadding;
  }

  private getContentWidth(): number {
    if (this.tappers.length === 0) return 0;
    return this.edgePadding * 2 + this.tappers.length * this.tapperWidth + (this.tappers.length - 1) * this.gap;
  }

  private clampScrollX(x: number): number {
    const minX = this.getMinScrollX();
    const maxX = this.getMaxScrollX();
    return Math.max(minX, Math.min(maxX, x));
  }

  private layoutTappers(): void {
    if (this.tappers.length === 0) return;

    // Calculate actual content width (tappers + gaps, without edge padding)
    const actualContentWidth =
      this.tappers.length * this.tapperWidth + (this.tappers.length - 1) * this.gap;

    // Center content if it fits in viewport, otherwise use edgePadding for scrolling
    let startX: number;
    if (actualContentWidth + this.edgePadding * 2 <= this.viewportWidth) {
      startX = (this.viewportWidth - actualContentWidth) / 2;
    } else {
      startX = this.edgePadding;
    }

    let currentX = startX;
    for (const tapper of this.tappers) {
      tapper.x = currentX;
      tapper.y = 0;
      currentX += this.tapperWidth + this.gap;
    }
  }

  private draw(): void {
    // Draw background
    this.background.clear();
    if (this.style.backgroundAlpha > 0) {
      this.background.rect(0, 0, this.viewportWidth, this.viewportHeight);
      this.background.fill({ color: this.style.backgroundColor, alpha: this.style.backgroundAlpha });
    }

    // Draw mask
    this.contentMask.clear();
    this.contentMask.rect(0, 0, this.viewportWidth, this.viewportHeight);
    this.contentMask.fill(0xffffff);
  }
}
